
import type { ViewOptions } from "./view";
import type { 
    AbstractEngine,
    AbstractEngineOptions,
    EngineOptions,
    WebGPUEngineOptions 
} from "@babylonjs/core";

import { View } from "./view";

//see Union Types and Intersection Types https://www.typescriptlang.org/docs/handbook/2/objects.html
//https://www.typescriptlang.org/docs/handbook/2/everyday-types.html#union-types

export type ViewerOptions = ViewOptions & (
    ({ engine?: undefined } & AbstractEngineOptions) |
    ({ engine: "WebGPU" } & WebGPUEngineOptions) |
    ({ engine: "WebGL" } & EngineOptions)
);

const defaultViewerOptions: ViewerOptions = {
    stencil: true
};

export class Viewer
{
    private _views = new Map<string, View>();
    private static _viewer: Viewer;

    private constructor() {        
    }

    public static Instance(): Viewer {

        if (!this._viewer)
            this._viewer = new Viewer();
        return this._viewer;
    }

    public GetDefaultEngine(): NonNullable<ViewerOptions["engine"]> {
        // TODO: When WebGPU is fully production ready, we may want to prefer it if it is supported by the browser.
        return "WebGL";
    }
    
    
    public async CreateViewForCanvas(canvas: HTMLCanvasElement, options?: ViewerOptions): Promise<View> {
        const finalOptions = { ...defaultViewerOptions, ...options };
        const disposeActions: (() => void)[] = [];
    
        // If the canvas is resized, note that the engine needs a resize, but don't resize it here as it will result in flickering.
        let needsResize = false;
        const resizeObserver = new ResizeObserver(() => (needsResize = true));
        resizeObserver.observe(canvas);
        disposeActions.push(() => resizeObserver.disconnect());
        
        //powerPreference?: GPUPowerPreference;
        const n: WebGPUEngineOptions = {};
        n.powerPreference = "high-performance";
        const o: EngineOptions = {};
        o.powerPreference = "high-performance";
        
        type ono = WebGPUEngineOptions & EngineOptions;
        const on: ono = {}

        // Create an engine instance.
        let engine: AbstractEngine;
        switch (finalOptions.engine ?? this.GetDefaultEngine()) {
            case "WebGL": {
                const { Engine } = await import("@babylonjs/core/Engines/engine");
                engine = new Engine(canvas, true, on);
                break;
            }
            case "WebGPU": {
                const { WebGPUEngine } = await import("@babylonjs/core/Engines/webgpuEngine");
                const webGPUEngine = new WebGPUEngine(canvas, on);
                await webGPUEngine.initAsync();
                engine = webGPUEngine;
                break;
            }
        }
    
        // Override the onInitialized callback to add in some specific behavior.
        const onInitialized = finalOptions.onInitialized;
        finalOptions.onInitialized = (details) => {
            // Resize if needed right before rendering the Viewer scene to avoid any flickering.
            const beforeRenderObserver = details.scene.onBeforeRenderObservable.add(() => {
                if (needsResize) {
                    engine.resize();
                    needsResize = false;
                }
            });
            disposeActions.push(() => beforeRenderObserver.remove());
    
            // Call the original onInitialized callback, if one was provided.
            onInitialized?.(details);
        };
    
        // Instantiate the Viewer with the engine and options.
        const view = await View.CreateView(engine, canvas, canvas, finalOptions);
        disposeActions.push(view.dispose.bind(view));
    
        disposeActions.push(() => engine.dispose());
    
        // Override the Viewer's dispose method to add in additional cleanup.
        view.dispose = () => disposeActions.forEach((dispose) => dispose());
    
        return view;
    }
    
    public GetOrCreateView(uniqueId: string): View
    {
        let view = this._views.get(uniqueId);
        if (view) return view;

        view = new View(uniqueId);
        
        this._views.set(uniqueId, view);

        return view;
    }
}
